/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useEffect, useMemo } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    CircularProgress,
    Switch,
    FormControlLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useToast, { ToastType } from '../../hooks/useToast';
import { CreateStaffPayload, StaffRole, StaffRoleLevels, UpdateStaffPayload } from '../../types/Staff';
import { useAuth } from '../../context/AuthContext';
import useStaffService from '../../services/administration/useStaffService';
import { getSelectableRoles } from '../../utils/user';
import { DialogMode } from '../../types/DialogTypes';

export type StaffDialogFields = {
    staffId?: number;
    name: string;
    phoneNumber: string;
    email?: string;
    password?: string;
    licensePlate?: string;
    receivesCustomerAlerts: boolean;
    receivesAdminMessages: boolean;
    autoCheckinWithANPR: boolean;
    usesWhatsappForAlerts: boolean;
    canAccessReports: boolean;
    canAccessAdministration: boolean;
    receivesOwnerMessages: boolean;
    role: StaffRole;
};

interface StaffDialogProps {
    open: boolean;
    onClose: () => void;
    mode?: DialogMode;
    initialValues?: Partial<StaffDialogFields>;
    isCurrentUser?: boolean;
};

const DEFAULT_FIELDS: StaffDialogFields = {
    name: '',
    phoneNumber: '',
    email: '',
    password: '',
    licensePlate: '',
    receivesCustomerAlerts: true,
    receivesAdminMessages: false,
    autoCheckinWithANPR: false,
    usesWhatsappForAlerts: true,
    canAccessReports: false,
    canAccessAdministration: false,
    receivesOwnerMessages: false,
    role: StaffRole.EMPLOYEE,
};

const createStaffDialogSchema = (t: (key: string) => string, isEditModeWithNoInitialEmail: boolean) => yup.object({
    name: yup.string().trim().required(),
    phoneNumber: yup.string().trim().required(),
    role: yup.mixed<StaffRole>().oneOf(Object.values(StaffRole)).required(),
    staffId: yup.number().optional(),
    email: yup.string().matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, { message: t('administration.staffDialog.errors.invalidEmail'), excludeEmptyString: true }).optional(),
    password: yup.string().when('email', {
        is: (email: string) => isEditModeWithNoInitialEmail && !!email?.trim(),
        then: (schema) => schema.trim().required(t('administration.staffDialog.errors.passwordRequiredWithEmail')),
        otherwise: (schema) => schema.optional(),
    }),
    licensePlate: yup.string().optional(),
    receivesCustomerAlerts: yup.boolean().required(),
    receivesAdminMessages: yup.boolean().required(),
    autoCheckinWithANPR: yup.boolean().required(),
    usesWhatsappForAlerts: yup.boolean().required(),
    canAccessReports: yup.boolean().required(),
    canAccessAdministration: yup.boolean().required(),
    receivesOwnerMessages: yup.boolean().required(),
});


function StaffDialog({
    open,
    onClose,
    mode = DialogMode.CREATE,
    initialValues,
    isCurrentUser = false,
}: StaffDialogProps) {
    const { t } = useTranslation("common");
    const { activeLocation } = useAuth();
    const { showToast } = useToast();
    const { createStaffMember, updateStaffMember } = useStaffService();

    const activeLocationId = activeLocation?.id ?? null;

    const isViewMode = mode === DialogMode.VIEW;
    const isEditMode = mode === DialogMode.EDIT;
    const isCreateMode = mode === DialogMode.CREATE;

    const staffDialogSchema = useMemo(
        () => createStaffDialogSchema(t, isEditMode && !initialValues?.email),
        [t, isEditMode, initialValues?.email],
    );

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<StaffDialogFields>({
        resolver: yupResolver(staffDialogSchema) as Resolver<StaffDialogFields>,
        defaultValues: DEFAULT_FIELDS,
        mode: 'onTouched',
    });

    const [watchedEmail, watchedRole] = watch(['email', 'role']);

    useEffect(() => {
        if (open) {
            reset({ ...DEFAULT_FIELDS, ...initialValues });
        }
    }, [open, initialValues, reset]);

    const roleLabels: Record<StaffRole, string> = useMemo(() => ({
        [StaffRole.ADMIN]: t('common.role.admin'),
        [StaffRole.OWNER]: t('common.role.owner'),
        [StaffRole.EMPLOYEE]: t('common.role.employee'),
    }), [t]);

    const dialogTitle = useMemo(() => {
        switch (mode) {
            case DialogMode.VIEW:
                return t('administration.staffDialog.title.view-staff');
            case DialogMode.EDIT:
                return t('administration.staffDialog.title.edit-staff');
            case DialogMode.CREATE:
            default:
                return t('administration.staffDialog.title.create-staff');
        }
    }, [mode, t]);

    const handleCreateStaffMember = async (values: StaffDialogFields) => {
        if (!activeLocationId) {
            showToast({ message: t('common.unknownError'), type: ToastType.ERROR });
            return;
        }

        const hasEmail = !!values.email?.trim();
        const hasPassword = !!values.password?.trim();

        if (hasEmail && !hasPassword) {
            setError('password', { message: t('administration.staffDialog.errors.passwordRequiredWithEmail') });
            return;
        }
        if (hasPassword && !hasEmail) {
            setError('email', { message: t('administration.staffDialog.errors.emailRequiredWithPassword') });
            return;
        }

        const payload: CreateStaffPayload = {
            ...values,
            email: values.email?.trim() || undefined,
            password: values.password?.trim() || undefined,
            licensePlate: values.licensePlate?.trim() || undefined,
            locationId: activeLocationId,
        };

        try {
            await createStaffMember(payload);
            showToast({ message: t('administration.staff.create-staff-success'), type: ToastType.SUCCESS });
        } catch {
            showToast({ message: t('common.unknownError'), type: ToastType.ERROR });
        } finally {
            onClose();
        }
    };

    const handleEditStaffMember = async (values: StaffDialogFields) => {
        if (!activeLocationId || !values.staffId) {
            showToast({ message: t('common.unknownError'), type: ToastType.ERROR });
            return;
        }

        const payload: UpdateStaffPayload = {
            ...values,
            email: values.email?.trim() || undefined,
            password: values.password?.trim() || undefined,
            licensePlate: values.licensePlate?.trim() || undefined,
            locationId: activeLocationId,
            needsEnroll: initialValues?.phoneNumber !== values.phoneNumber,
            role: initialValues?.role !== values.role ? values.role : undefined,
            staffId: values.staffId,
        };

        try {
            await updateStaffMember(payload);
            showToast({ message: t('administration.staff.edit-staff-success'), type: ToastType.SUCCESS });
        } catch {
            showToast({ message: t('common.unknownError'), type: ToastType.ERROR });
        } finally {
            onClose();
        }
    };

    const showPasswordField = isCreateMode || (isEditMode && !!watchedEmail?.trim() && !initialValues?.email);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3} mt={1}>
                    {/* LEFT COLUMN: Basic Information */}
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                            <Box fontWeight={600} mb={2} fontSize="1rem">
                                {t('administration.staffDialog.dialog.sections.basicInfo')}
                            </Box>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label={t('administration.staffDialog.fields.name')}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error && t('common.completeMandatoryFields')}
                                            disabled={isViewMode}
                                            fullWidth
                                            variant="outlined"
                                            required
                                        />
                                    )}
                                />
                                <Controller
                                    name="phoneNumber"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label={t('administration.staffDialog.fields.phoneNumber')}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error && t('common.completeMandatoryFields')}
                                            fullWidth
                                            variant="outlined"
                                            disabled={isViewMode || (!!initialValues?.phoneNumber && !isEditMode)}
                                            required
                                        />
                                    )}
                                />
                                <Controller
                                    name="licensePlate"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t('administration.staffDialog.fields.licensePlate')}
                                            fullWidth
                                            variant="outlined"
                                            disabled={isViewMode}
                                        />
                                    )}
                                />
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label={t('administration.staffDialog.fields.email')}
                                            type="email"
                                            fullWidth
                                            variant="outlined"
                                            disabled={isViewMode}
                                            error={!!fieldState.error || !!errors.root}
                                            helperText={fieldState.error?.message}
                                            onChange={e => {
                                                field.onChange(e);
                                                clearErrors('root');
                                                clearErrors('email');
                                                if (isEditMode && !e.target.value.trim() && !initialValues?.email) {
                                                    setValue('password', '');
                                                }
                                            }}
                                        />
                                    )}
                                />
                                {showPasswordField && (
                                    <Controller
                                        name="password"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                label={t('administration.staffDialog.fields.password')}
                                                fullWidth
                                                variant="outlined"
                                                type="password"
                                                error={!!fieldState.error || !!errors.root}
                                                helperText={fieldState.error?.message ?? errors.root?.message}
                                                onChange={e => {
                                                    field.onChange(e);
                                                    clearErrors('root');
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* RIGHT COLUMN: Notifications + Permissions */}
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Role for Current Location Section */}
                        {!isCurrentUser && (
                            <Box>
                                <Controller
                                    name="role"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup
                                            row
                                            name={`location-role-${activeLocationId}`}
                                            value={field.value}
                                            onChange={e => {
                                                const newRole = e.target.value as StaffRole;
                                                field.onChange(newRole);
                                                if (newRole === StaffRole.EMPLOYEE) {
                                                    setValue('receivesOwnerMessages', false);
                                                }
                                                if (newRole !== StaffRole.ADMIN) {
                                                    setValue('receivesAdminMessages', false);
                                                }
                                            }}
                                        >
                                            {getSelectableRoles(activeLocation?.rolelevel ?? 0).map(roleValue => (
                                                <FormControlLabel
                                                    key={roleValue}
                                                    value={roleValue}
                                                    control={<Radio disabled={isViewMode} />}
                                                    label={roleLabels[roleValue]}
                                                    disabled={isViewMode}
                                                />
                                            ))}
                                        </RadioGroup>
                                    )}
                                />
                            </Box>
                        )}

                        {/* Notifications Section */}
                        <Box>
                            <Box fontWeight={600} mb={2} fontSize="1rem">
                                {t('administration.staffDialog.sections.notifications')}
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Controller
                                    name="receivesCustomerAlerts"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={e => field.onChange(e.target.checked)}
                                                    disabled={isViewMode}
                                                />
                                            }
                                            label={t('administration.staffDialog.fields.receivesCustomerAlerts')}
                                        />
                                    )}
                                />
                                <Controller
                                    name="receivesOwnerMessages"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={e => field.onChange(e.target.checked)}
                                                    disabled={isViewMode || watchedRole === StaffRole.EMPLOYEE}
                                                />
                                            }
                                            label={t('administration.staffDialog.fields.receivesOwnerMessages')}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>

                        {/* Permissions & Features Section */}
                        {(activeLocation?.rolelevel ?? 0) >= StaffRoleLevels.admin && (
                            <Box>
                                <Box fontWeight={600} mb={2} fontSize="1rem">
                                    {t('administration.staffDialog.sections.permissionsFeatures')}
                                </Box>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Controller
                                        name="canAccessReports"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={e => field.onChange(e.target.checked)}
                                                        disabled={isViewMode}
                                                    />
                                                }
                                                label={t('administration.staffDialog.fields.canAccessReports')}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="canAccessAdministration"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={e => field.onChange(e.target.checked)}
                                                        disabled={isViewMode}
                                                    />
                                                }
                                                label={t('administration.staffDialog.fields.canAccessAdministration')}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="autoCheckinWithANPR"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={e => field.onChange(e.target.checked)}
                                                        disabled={isViewMode}
                                                    />
                                                }
                                                label={t('administration.staffDialog.fields.autoCheckinWithANPR')}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="receivesAdminMessages"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={e => field.onChange(e.target.checked)}
                                                        disabled={isViewMode || watchedRole !== StaffRole.ADMIN}
                                                    />
                                                }
                                                label={t('administration.staffDialog.fields.receivesAdminMessages')}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{isViewMode ? t('common.close') : t('common.cancel')}</Button>
                {!isViewMode && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleSubmit(isEditMode ? handleEditStaffMember : handleCreateStaffMember)}
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? <CircularProgress size={24} />
                            : (isEditMode ? t('common.save') : t('administration.locationAdministration.addStaff'))
                        }
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default StaffDialog;
